'use client';

import Link from 'next/link';

import { CopyrightYear } from '@/components/ui/copyright-year';

import { TalkWithUsButton } from '@/components/support';
import { useHiddenComponentByPage } from '@/hooks/use-hidden-component-page';
import { cn } from '@/shared/utils/formatting';

const COMPANY_LINKS = [
  {
    href: '/creators',
    label: 'Creators',
    external: false,
  },
  {
    href: 'https://taboo.tv/become-creator',
    label: 'Become a Creator',
    external: true,
  },
  {
    href: '/courses',
    label: 'Courses',
    external: false,
  },
] as const;

const SUPPORT_LINKS = [
  { href: 'https://taboo.tv/contact-us', label: 'Contact Us', external: true },
  {
    href: 'https://taboo.tv/refund-policy',
    label: 'Refund Policy',
    external: true,
  },
] as const;

const LEGAL_LINKS = [
  {
    href: 'https://taboo.tv/terms-conditions',
    label: 'Terms & Conditions',
    external: true,
  },
  {
    href: 'https://taboo.tv/privacy-policy',
    label: 'Privacy Policy',
    external: true,
  },
] as const;

const SOCIAL_LINKS = [
  {
    href: 'https://x.com/tabootv',
    label: 'Follow on X (Twitter)',
    Icon: XIcon,
  },
  {
    href: 'https://www.instagram.com/tabootv/',
    label: 'Follow on Instagram',
    Icon: InstagramIcon,
  },
] as const;

const STORE_LINKS = [
  {
    href: 'https://apps.apple.com/br/app/taboo-tv/id6738045672?l=en-GB',
    sublabel: 'Download on the',
    storeName: 'App Store',
    icon: 'apple' as const,
  },
  {
    href: 'https://play.google.com/store/apps/details?id=com.tvtaboo&pcampaignid=web_share',
    sublabel: 'Get it on',
    storeName: 'Google Play',
    icon: 'google' as const,
  },
] as const;

function XIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function InstagramIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="shrink-0 text-foreground"
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="shrink-0 text-foreground"
    >
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302L15.396 12l2.302-2.492zM5.864 2.658L16.8 8.991l-2.302 2.302-8.634-8.635z" />
    </svg>
  );
}

function FooterColumn({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-5 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-label">
        {label}
        <span className="h-px max-w-10 flex-1 bg-border-subtle" />
      </div>
      {children}
    </div>
  );
}

function FooterNavLink({
  href,
  children,
  external,
}: {
  href: string;
  children: React.ReactNode;
  external?: boolean;
}) {
  return (
    <li>
      <Link
        href={href}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className="group flex items-center gap-1.5 py-1.75 text-[13.5px] font-normal text-text-secondary transition-all duration-200 hover:pl-1.5 hover:text-foreground focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {children}
        <span className="text-[11px] text-accent opacity-0 transition-all duration-200 -translate-x-1 group-hover:translate-x-0 group-hover:opacity-100">
          &rarr;
        </span>
      </Link>
    </li>
  );
}

interface FooterProps {
  classNameDivContainer?: string;
}

export function Footer({ classNameDivContainer }: FooterProps = {}) {
  const isHidden = useHiddenComponentByPage(['/shorts']);

  if (isHidden) return null;

  return (
    <footer role="contentinfo" className="relative overflow-hidden border-t border-border-subtle">
      <div
        className="pointer-events-none absolute top-0 left-1/2 h-px w-[600px] -translate-x-1/2 opacity-40"
        style={{
          background: 'linear-gradient(90deg, transparent, var(--color-accent), transparent)',
        }}
      />

      <div className={cn('px-4 pt-14 max-md:pt-10', classNameDivContainer)}>
        <nav
          aria-label="Footer navigation"
          className="animate-fade-up grid grid-cols-1 gap-7 pb-10 md:grid-cols-2 md:gap-9 lg:grid-cols-[1.2fr_1fr_1fr_1.1fr] lg:gap-10"
        >
          <FooterColumn label="Company">
            <ul className="flex flex-col gap-1">
              {COMPANY_LINKS.map(({ href, label, external }) => (
                <FooterNavLink
                  key={href}
                  href={href}
                  {...(external !== undefined ? { external } : {})}
                >
                  {label}
                </FooterNavLink>
              ))}
            </ul>

            <TalkWithUsButton source="footer" variant="footer" />
          </FooterColumn>

          <FooterColumn label="Support">
            <ul className="flex flex-col gap-1">
              {SUPPORT_LINKS.map(({ href, label, external }) => (
                <FooterNavLink
                  key={href}
                  href={href}
                  {...(external !== undefined ? { external } : {})}
                >
                  {label}
                </FooterNavLink>
              ))}
            </ul>
          </FooterColumn>

          <FooterColumn label="Legal">
            <ul className="flex flex-col gap-1">
              {LEGAL_LINKS.map(({ href, label, external }) => (
                <FooterNavLink
                  key={href}
                  href={href}
                  {...(external !== undefined ? { external } : {})}
                >
                  {label}
                </FooterNavLink>
              ))}
            </ul>
          </FooterColumn>

          <FooterColumn label="Get the App">
            <div className="mt-1 flex flex-col gap-2.5">
              {STORE_LINKS.map(({ href, sublabel, storeName, icon }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex min-h-12 items-center gap-3 rounded-[10px] border border-border-subtle bg-white/4 px-3.5 py-2.5 transition-all duration-200 hover:-translate-y-px hover:border-border-hover hover:bg-white/7 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent max-md:max-w-[220px]"
                >
                  {icon === 'apple' ? <AppleIcon /> : <GooglePlayIcon />}
                  <div className="flex flex-col">
                    <span className="text-[9.5px] font-medium uppercase leading-none tracking-[0.03em] text-text-muted">
                      {sublabel}
                    </span>
                    <span className="text-sm font-medium leading-snug text-foreground">
                      {storeName}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </FooterColumn>
        </nav>

        <div className="animate-fade-up flex flex-col items-start gap-4 border-t border-border-subtle py-6 [animation-delay:0.1s]">
          <div className="flex items-center gap-5">
            {SOCIAL_LINKS.map(({ href, label, Icon }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary transition-colors duration-200 hover:text-foreground focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>

          <span className="text-xs text-text-muted">
            &copy; <CopyrightYear /> Taboo TV Studios LLC &middot; Georgia, USA
          </span>
        </div>
      </div>
    </footer>
  );
}
